/**
 * CHIVI™ — CHIVIQUE assistant widget
 * Vanilla JS, zero dependencies, zero build step. Loaded as a Shopify theme
 * asset with `defer`. Mounts after first idle/scroll so it never competes
 * with LCP.
 *
 * Scope note (documented, not silent): V1 ships /chat as a single JSON
 * round-trip rather than token-by-token SSE streaming. Claude's tool-use
 * loop (catalog lookups happen server-side before the final answer is ready)
 * makes true streaming a second-phase upgrade — see chivi/docs/DEPLOY.md.
 * Typing indicator covers the wait, which is sub-2s for Haiku at this
 * catalog size.
 */
(function () {
  "use strict";

  // ── Configuration — set this after your Worker is deployed ───────────
  var CONFIG = {
    API_BASE: window.CHIVI_API_BASE || "https://chivi-concierge.YOUR_SUBDOMAIN.workers.dev",
    NUDGE_DELAY_MS: 12000,
    MOUNT_DELAY_MS: 4000,
  };

  if (document.getElementById("chivi-root")) return; // idempotent guard

  // ── Utilities ───────────────────────────────────────────────────────────────
  function uuid() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0,
        v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function getAnonId() {
    var id = localStorage.getItem("chivi_anon_id");
    if (!id) {
      id = uuid();
      localStorage.setItem("chivi_anon_id", id);
    }
    return id;
  }

  function getConsent() {
    return localStorage.getItem("chivi_consent") === "true";
  }
  function setConsent(val) {
    localStorage.setItem("chivi_consent", val ? "true" : "false");
  }

  function getSessionId() {
    var id = sessionStorage.getItem("chivi_session_id");
    if (!id) {
      id = uuid();
      sessionStorage.setItem("chivi_session_id", id);
    }
    return id;
  }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    attrs = attrs || {};
    for (var k in attrs) {
      if (k === "class") node.className = attrs[k];
      else if (k === "html") node.innerHTML = attrs[k];
      else if (k.indexOf("on") === 0) node.addEventListener(k.slice(2), attrs[k]);
      else node.setAttribute(k, attrs[k]);
    }
    (children || []).forEach(function (c) {
      if (typeof c === "string") node.appendChild(document.createTextNode(c));
      else if (c) node.appendChild(c);
    });
    return node;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  // ── Mascot SVG — "the CHIVI Pearl" ─────────────────────────────────────────────
  function mascotSvg(size) {
    size = size || 40;
    return (
      '<svg class="chivi-mascot-svg" width="' + size + '" height="' + size + '" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">' +
      '<g class="chivi-mascot-glow">' +
      '<ellipse class="chivi-mascot-halo" cx="50" cy="50" rx="38" ry="14" fill="none" stroke="#4338ca" stroke-width="2.5" opacity="0.75" transform="rotate(-18 50 50)"/>' +
      '<circle class="chivi-mascot-body" cx="50" cy="52" r="26" fill="url(#chiviPearlGrad)"/>' +
      '<ellipse cx="41" cy="43" rx="8" ry="5" fill="#ffffff" opacity="0.55"/>' +
      '<ellipse class="chivi-mascot-eye" cx="42" cy="54" rx="2.6" ry="4" fill="#172033"/>' +
      '<ellipse class="chivi-mascot-eye" cx="58" cy="54" rx="2.6" ry="4" fill="#172033"/>' +
      '<path d="M 43 63 Q 50 67 57 63" stroke="#172033" stroke-width="1.6" fill="none" stroke-linecap="round" opacity="0.7"/>' +
      '<g class="chivi-mascot-sparkle" style="transform-origin:50px 50px">' +
      '<path d="M50 8 L52 14 L58 16 L52 18 L50 24 L48 18 L42 16 L48 14 Z" fill="#4338ca"/>' +
      "</g>" +
      "</g>" +
      "<defs>" +
      '<radialGradient id="chiviPearlGrad" cx="35%" cy="30%" r="75%">' +
      '<stop offset="0%" stop-color="#ffffff"/>' +
      '<stop offset="55%" stop-color="#f1f3f7"/>' +
      '<stop offset="100%" stop-color="#c7d2fe"/>' +
      "</radialGradient>" +
      "</defs>" +
      "</svg>"
    );
  }

  // ── State ───────────────────────────────────────────────────────────────────
  var state = {
    anonId: getAnonId(),
    sessionId: getSessionId(),
    consent: getConsent(),
    open: false,
    sending: false,
    recentlyViewed: JSON.parse(localStorage.getItem("chivi_rv") || "[]"),
  };

  // ── Recently viewed tracking (product pages only) ─────────────────
  (function trackRecentlyViewed() {
    var ctx = window.CHIVI_CONTEXT;
    if (!ctx || !ctx.product) return;
    var rv = state.recentlyViewed.filter(function (h) {
      return h !== ctx.product.handle;
    });
    rv.unshift(ctx.product.handle);
    rv = rv.slice(0, 10);
    state.recentlyViewed = rv;
    localStorage.setItem("chivi_rv", JSON.stringify(rv));
  })();

  // ── Analytics beacon ─────────────────────────────────────────────────────────────
  function track(type, payload) {
    var body = JSON.stringify({
      session_id: state.sessionId,
      anon_id: state.anonId,
      type: type,
      payload: payload || {},
    });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(CONFIG.API_BASE + "/events", new Blob([body], { type: "application/json" }));
    } else {
      fetch(CONFIG.API_BASE + "/events", { method: "POST", body: body, headers: { "Content-Type": "application/json" }, keepalive: true });
    }
  }

  // ── Checkout suppression ────────────────────────────────────────────────────────────
  function isCheckoutPath() {
    return /^\/checkout/.test(window.location.pathname);
  }

  // ── Cart helpers (Shopify Ajax API) ───────────────────────────────────────────────
  function getCart() {
    return fetch("/cart.js").then(function (r) {
      return r.json();
    });
  }

  function addToCart(sku) {
    return fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [{ id: sku, quantity: 1 }] }),
    }).then(function (r) {
      return r.json();
    });
  }

  // Tags the cart with this session's anon_id so the orders/create webhook
  // can attribute a completed purchase back to the chat that influenced it
  // (Bug-5 fix, CHIVI.1.1 patch — this was previously never set, so revenue
  // attribution silently matched nothing). Shopify carries cart `attributes`
  // through to the resulting order as `note_attributes`, which is exactly
  // what sync.ts's order webhook handler already reads.
  function tagCartWithAnonId() {
    fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: { chivi_anon_id: state.anonId } }),
    }).catch(function () {});
  }

  // ── Build UI ─────────────────────────────────────────────────────────────────────
  var root, launcher, panel, body, input, sendBtn, nudge, mascotLauncherEl;

  function buildWidget() {
    root = el("div", { id: "chivi-root" });

    nudge = el("div", { class: "chivi-nudge" }, ["Explore digital products with CHIVI ✨"]);

    launcher = el(
      "button",
      {
        class: "chivi-launcher",
        "aria-label": "Open CHIVI, the CHIVIQUE assistant",
        "aria-haspopup": "dialog",
        onclick: togglePanel,
      },
      []
    );
    launcher.innerHTML = mascotSvg(38);
    mascotLauncherEl = launcher.querySelector(".chivi-mascot-svg");

    panel = buildPanel();

    root.appendChild(nudge);
    root.appendChild(launcher);
    root.appendChild(panel);
    document.body.appendChild(root);

    maybeShowNudge();
    tagCartWithAnonId();
    document.addEventListener("visibilitychange", function () {
      document.getElementById("chivi-root").classList.toggle("chivi-tab-hidden", document.hidden);
    });
  }

  function buildPanel() {
    var p = el("div", { class: "chivi-panel", role: "dialog", "aria-modal": "true", "aria-label": "CHIVI chat" });

    var header = el("div", { class: "chivi-header" }, [
      el("div", { class: "chivi-header-left" }, [
        (function () {
          var d = el("div", {});
          d.innerHTML = mascotSvg(28);
          return d;
        })(),
        el("div", { class: "chivi-wordmark" }, ["CHIVI", el("small", {}, ["CHIVIQUE assistant"])]),
      ]),
      el("button", { class: "chivi-close", "aria-label": "Close", onclick: closePanel }, ["✕"]),
    ]);

    body = el("div", { class: "chivi-body" }, []);
    renderWelcome();

    var inputRow = el("div", { class: "chivi-input-row" }, []);
    input = el("input", {
      type: "text",
      placeholder: "Ask about a digital product…",
      "aria-label": "Message CHIVI",
      onkeydown: function (e) {
        if (e.key === "Enter") sendMessage();
      },
    });
    sendBtn = el(
      "button",
      { class: "chivi-send", "aria-label": "Send", onclick: sendMessage },
      []
    );
    sendBtn.innerHTML = "→";
    inputRow.appendChild(input);
    inputRow.appendChild(sendBtn);

    var consentFooter = el("div", { class: "chivi-consent-footer" }, []);
    consentFooter.innerHTML = consentFooterHtml();

    p.appendChild(header);
    p.appendChild(body);
    p.appendChild(inputRow);
    p.appendChild(consentFooter);

    setTimeout(function () {
      var forgetBtn = document.getElementById("chivi-forget");
      var allowBtn = document.getElementById("chivi-consent-yes");
      if (forgetBtn) forgetBtn.addEventListener("click", forgetMe);
      if (allowBtn) allowBtn.addEventListener("click", allowConsent);
    }, 0);

    return p;
  }

  function forgetMe() {
    fetch(CONFIG.API_BASE + "/profile", {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ anon_id: state.anonId }),
    });
    setConsent(false);
    state.consent = false;
    localStorage.removeItem("chivi_rv");
    state.recentlyViewed = [];
    refreshConsentFooter();
  }

  function allowConsent() {
    setConsent(true);
    state.consent = true;
    refreshConsentFooter();
  }

  function consentFooterHtml() {
    return state.consent
      ? 'CHIVI remembers your product interests to serve you better. <button id="chivi-forget">Forget me</button>'
      : 'CHIVI can remember your product interests. <button id="chivi-consent-yes">Allow</button>';
  }

  function refreshConsentFooter() {
    var footer = panel.querySelector(".chivi-consent-footer");
    footer.innerHTML = consentFooterHtml();
    var forgetBtn = document.getElementById("chivi-forget");
    var allowBtn = document.getElementById("chivi-consent-yes");
    if (forgetBtn) forgetBtn.addEventListener("click", forgetMe);
    if (allowBtn) allowBtn.addEventListener("click", allowConsent);
  }

  function renderWelcome() {
    var ctx = window.CHIVI_CONTEXT || { page_type: "index" };
    var welcome = el("div", { class: "chivi-welcome" }, []);
    var mascotWrap = el("div", { class: "chivi-mascot-large chivi-mascot chivi-greet" }, []);
    mascotWrap.innerHTML = mascotSvg(72);

    var cats = ctx.categories || {};
    var digitalLive = !!(cats.digital_marketplace && cats.digital_marketplace.published > 0);
    var heading = "Hi, I’m CHIVI.";
    var sub = "Hi, I’m CHIVI, your CHIVIQUE assistant. Tell me what you want to create, learn, or get done, and I’ll help you explore our digital products.";
    if (ctx.product) sub += " Ask me about the published details for " + ctx.product.title + ".";

    welcome.appendChild(mascotWrap);
    welcome.appendChild(el("h3", {}, [heading]));
    welcome.appendChild(el("p", {}, [sub]));

    var quick = [
      quickActionBtn("Explore products", digitalLive ? "Which digital products are published, and what do they include?" : "Are any digital products available yet?"),
    ];
    quick.push(quickActionBtn("Downloads & refunds", "Where can I find digital download and refund information?"));
    quick.push(quickActionBtn("Formats & compatibility", "What file formats and software requirements are published for the toolkit?", true));
    quick.push(quickActionBtn("Licensing", "Where can I find the published license and permitted uses for the toolkit?"));
    quick.push(quickActionBtn("Contact CHIVIQUE", "Where can I contact CHIVIQUE for download help?"));
    welcome.appendChild(el("div", { class: "chivi-quick-actions" }, quick));

    body.appendChild(welcome);
  }

  function quickActionBtn(label, message, full) {
    return el(
      "button",
      {
        class: full ? "chivi-full" : "",
        onclick: function () {
          input.value = message;
          sendMessage();
        },
      },
      [label]
    );
  }

  // ── Panel open/close ───────────────────────────────────────────────────────────────────
  function togglePanel() {
    if (state.open) closePanel();
    else openPanel();
  }

  function openPanel() {
    state.open = true;
    panel.classList.add("chivi-open");
    nudge.classList.remove("chivi-visible");
    input.focus();
    track("widget_opened", { page_type: (window.CHIVI_CONTEXT || {}).page_type });
    document.addEventListener("keydown", onKeyDown);
  }

  function closePanel() {
    state.open = false;
    panel.classList.remove("chivi-open");
    document.removeEventListener("keydown", onKeyDown);
    launcher.focus();
  }

  function onKeyDown(e) {
    if (e.key === "Escape") {
      closePanel();
      return;
    }
    // Focus trap: keep Tab/Shift+Tab cycling within the panel while open,
    // instead of letting focus escape onto the page behind it (a11y fix,
    // CHIVI.1.1 patch). Purely keyboard behavior — no visual/UI change.
    if (e.key === "Tab") {
      var focusables = panel.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])');
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  function maybeShowNudge() {
    var ctx = window.CHIVI_CONTEXT || {};
    if (ctx.page_type !== "product") return;
    if (sessionStorage.getItem("chivi_nudge_shown")) return;
    setTimeout(function () {
      if (!state.open) {
        nudge.classList.add("chivi-visible");
        sessionStorage.setItem("chivi_nudge_shown", "1");
        setTimeout(function () {
          nudge.classList.remove("chivi-visible");
        }, 6000);
      }
    }, CONFIG.NUDGE_DELAY_MS);
  }

  // ── Messaging ─────────────────────────────────────────────────────────────────────
  function appendMessage(role, text) {
    var msg = el("div", { class: "chivi-msg chivi-msg-" + role }, [text]);
    body.appendChild(msg);
    body.scrollTop = body.scrollHeight;
    return msg;
  }

  function appendTyping() {
    var t = el("div", { class: "chivi-typing" }, []);
    t.innerHTML = "<span></span><span></span><span></span>";
    body.appendChild(t);
    body.scrollTop = body.scrollHeight;
    return t;
  }

  // Same-site links the assistant points to (policy pages, collections). Only
  // relative paths are rendered, so a payload can never send a customer off-site.
  function appendLinks(links) {
    var wrap = el("div", { class: "chivi-links", style: "display:flex;flex-wrap:wrap;gap:8px;margin:6px 0" }, []);
    (links || []).forEach(function (l) {
      if (!l || typeof l.url !== "string" || l.url.charAt(0) !== "/" || l.url.charAt(1) === "/") return;
      wrap.appendChild(el("a", { class: "chivi-btn-ghost", href: l.url }, [String(l.label || l.url)]));
    });
    if (wrap.childNodes.length) {
      body.appendChild(wrap);
      body.scrollTop = body.scrollHeight;
    }
  }

  function appendProductCard(p) {
    var card = el("div", { class: "chivi-product-card" }, []);
    var specBits = [];
    var vs = p.verified_specs || {};
    Object.keys(vs).slice(0, 3).forEach(function (k) {
      if (vs[k]) specBits.push(vs[k]);
    });
    var priceStr = p.price_min === p.price_max ? "₹" + p.price_min : "₹" + p.price_min + "–₹" + p.price_max;

    card.appendChild(el("p", { class: "chivi-product-title" }, [p.title]));
    if (specBits.length) card.appendChild(el("p", { class: "chivi-product-spec" }, [specBits.join(" · ")]));
    card.appendChild(el("p", { class: "chivi-product-price" }, [priceStr]));

    var actions = el("div", { class: "chivi-product-actions" }, []);
    actions.appendChild(
      el(
        "a",
        {
          class: "chivi-btn-ghost",
          href: "/products/" + p.handle,
          target: "_blank",
          rel: "noopener",
          onclick: function () {
            track("product_clicked", { title: p.title, handle: p.handle });
          },
        },
        ["View"]
      )
    );
    var atcBtn = el(
      "button",
      {
        class: "chivi-btn-solid",
        onclick: function () {
          atcBtn.disabled = true;
          atcBtn.textContent = "Adding…";
          var sku = p.variants && p.variants[0] ? p.variants[0].sku : null;
          addToCart(sku)
            .then(function () {
              atcBtn.textContent = "Added ✓";
              track("atc_after_chat", { title: p.title, sku: sku });
            })
            .catch(function () {
              atcBtn.disabled = false;
              atcBtn.textContent = "Add to Bag";
            });
        },
      },
      ["Add to Bag"]
    );
    actions.appendChild(atcBtn);
    card.appendChild(actions);
    body.appendChild(card);
    body.scrollTop = body.scrollHeight;
  }

  function appendHandoffCard(reason) {
    var wrap = el("div", { class: "chivi-handoff-card" }, []);
    wrap.innerHTML =
      "<strong>Let's get you the right help.</strong><p style='margin:6px 0 0'>" +
      escapeHtml(reason || "For this, our team can help directly.") +
      " — <a href='/pages/contact'>Contact CHIVIQUE</a></p>";
    body.appendChild(wrap);
    body.scrollTop = body.scrollHeight;
  }

  function marketplaceResponse(data) {
    data = data || {};
    var handles = (window.CHIVI_CONTEXT || {}).digital_product_handles || [];
    var staleCopy = /\b(clothing|apparel|fashion|outfits?|sizing|jewell?ery|necklaces?|earrings?|watches)\b|tell me what style you love most|(?:sell|selling|list|listing|onboard|register).{0,55}(?:through|on|with|at) CHIVIQUE|creator enquir|seller registration|independent creators|third.party sellers/i;
    if (staleCopy.test(String(data.reply || "")) || /(?:contact|join|apply|register|start).{0,60}(?:sell|seller|creator)|(?:sell|list) your (?:digital )?products/i.test(String(data.reply || ""))) {
      return {
        reply: "Explore digital products created and sold by CHIVIQUE. Our current offering is The Home Business Launch Toolkit. Contact CHIVIQUE for download help. External sellers and third-party listings are not accepted.",
        ui_payloads: [{ type: "links", links: [{ label: "Explore Digital Products", url: "/collections/digital-marketplace" }, { label: "Contact CHIVIQUE", url: "/pages/contact" }] }]
      };
    }
    data.ui_payloads = (data.ui_payloads || []).filter(function (payload) { return payload && payload.type !== "email_capture"; }).map(function (payload) {
      if (payload.type === "product_cards") {
        payload.products = (payload.products || []).filter(function (product) { return handles.indexOf(product.handle) !== -1; });
      } else if (payload.type === "links") {
        payload.links = (payload.links || []).filter(function (link) {
          return !staleCopy.test(String(link.label || "")) && !/sell|seller|creator|onboard/i.test(String(link.label || "")) && !/seller|creator|onboard|register/i.test(String(link.url || "")) && !/\/(?:collections|pages)\/(?:clothing|womens-fashion|jewellery|watches|necklaces|rings|earrings)/i.test(String(link.url || ""));
        });
      }
      return payload;
    });
    return data;
  }

  function sendMessage() {
    var text = input.value.trim();
    if (!text || state.sending) return;
    input.value = "";
    state.sending = true;
    sendBtn.disabled = true;

    // remove the welcome quick-actions block after first real message
    var welcome = body.querySelector(".chivi-welcome");
    if (welcome) welcome.remove();

    appendMessage("user", text);
    var typing = appendTyping();
    track("chat_started", {});

    getCart()
      .catch(function () {
        return { item_count: 0, total_price: 0, items: [] };
      })
      .then(function (cart) {
        var payload = {
          message: text,
          anon_id: state.anonId,
          session_id: state.sessionId,
          page_context: window.CHIVI_CONTEXT || { page_type: "index", currency: "INR" },
          cart: {
            item_count: cart.item_count,
            total_price: (cart.total_price || 0) / 100,
            items: (cart.items || []).map(function (i) {
              return { title: i.title, price: i.price / 100, quantity: i.quantity };
            }),
          },
          recently_viewed: state.recentlyViewed,
          consent_profile: state.consent,
        };

        return fetch(CONFIG.API_BASE + "/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }).then(function (r) {
          if (!r.ok) throw new Error("chat request failed: " + r.status);
          return r.json();
        });
      })
      .then(function (data) {
        data = marketplaceResponse(data);
        typing.remove();
        appendMessage("assistant", data.reply);
        (data.ui_payloads || []).forEach(function (payload) {
          if (payload.type === "product_cards") {
            (payload.products || []).forEach(appendProductCard);
          } else if (payload.type === "links") {
            appendLinks(payload.links);

          } else if (payload.type === "handoff_card") {
            appendHandoffCard(payload.reason);
          }
          // atc_prompt payload intentionally not auto-rendered as a separate
          // element — the product card's own "Add to Bag" button covers it.
        });
      })
      .catch(function () {
        // Server-side now catches provider failures and always returns 200
        // with a graceful reply (chat.ts) — this only fires on a true
        // network-level failure (offline, DNS, CORS). Must still never
        // expose that anything technical went wrong.
        typing.remove();
        appendMessage(
          "assistant",
          "I can't reach our assistant right now. You can explore the digital marketplace, or reach the team from the Contact page."
        );
      })
      .finally(function () {
        state.sending = false;
        sendBtn.disabled = false;
        input.focus();
      });
  }

  // ── Mount ──────────────────────────────────────────────────────────────────────
  var mountedOnce = false;
  function mount() {
    if (mountedOnce || isCheckoutPath()) return; // hard rule: never on checkout
    mountedOnce = true;
    buildWidget();
  }

  // Any element with [data-chivi-open] opens the assistant (mounting it first if needed).
  document.addEventListener("click", function (e) {
    var t = e.target && e.target.closest && e.target.closest("[data-chivi-open]");
    if (!t) return;
    e.preventDefault();
    mount();
    if (panel && !state.open) openPanel();
  });

  function scheduleMount() {
    var mounted = false;
    function doMount() {
      if (mounted) return;
      mounted = true;
      mount();
    }
    setTimeout(doMount, CONFIG.MOUNT_DELAY_MS);
    window.addEventListener("scroll", doMount, { once: true, passive: true });
  }

  if (document.readyState === "complete" || document.readyState === "interactive") {
    scheduleMount();
  } else {
    document.addEventListener("DOMContentLoaded", scheduleMount);
  }
})();

